import React, { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Avatar,
  Badge,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  Announcement as AnnouncementIcon,
} from "@mui/icons-material";
import axios from "axios";
import { requestID, baseUrl, config } from "../../../common/utils";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
// import { format } from "date-fns";

const NewsAlert = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [openModal, setOpenModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getNotifications = () => {
    const formData = new FormData();
    formData.append("requestID", requestID());
        formData.append("test", '12354');

    setStatus({ loading: true, error: false });

    axios
      .post(baseUrl() + "warehouses/getMessage", formData, config)
      .then((response) => {
        const allMessages = response.data.data || [];
        console.log(response);
        const notif = Array.isArray(allMessages) ? allMessages : [];
        setNotifications(notif);
        setUnreadCount(notif.filter((msg) => !msg.read).length);
        setStatus({ loading: false, error: false });
      })
      .catch((error) => {
        console.error("Failed to load notifications:", error);
        setNotifications([]);
        setUnreadCount(0);
        setStatus({ loading: false, error: true });
      });
  };

  useEffect(() => {
    getNotifications();
  }, []);

  const handleOpen = (message) => {
    if (!message.read) {
      const updatedNotifications = notifications.map((notif) =>
        notif.id === message.id ? { ...notif, read: true } : notif
      );
      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.filter((msg) => !msg.read).length);
    }

    setSelectedMessage(message);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedMessage(null);
  };

  const getIconByType = (type) => {
    switch (type) {
      case "transaction":
        return <EventNoteIcon color="primary" />;
      case "announcement":
        return <AnnouncementIcon color="secondary" />;
      case "maintenance":
        return <AnnouncementIcon color="warning" />;
      default:
        return <NotificationsIcon color="action" />;
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "EEEE, MMMM d, yyyy h:mm a");
    } catch {
      return dateString;
    }
  };


  const renderMessages = (messages) => {
    const sortedMessages = [...messages].sort(
      (a, b) =>
        new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    );

    return sortedMessages.map((item, i) => (
      <Card
        key={i}
        sx={{
          mb: 2,
          cursor: "pointer",
          "&:hover": { boxShadow: 3 },
          borderLeft: !item.read
            ? `4px solid ${theme.palette.primary.main}`
            : "none",
          backgroundColor: !item.read
            ? theme.palette.action.hover
            : "background.paper",
        }}
        onClick={() => handleOpen(item)}
      >
        <CardContent sx={{ display: "flex", gap: 2 }}>
          <Avatar sx={{ bgcolor: "transparent", mt: 1 }}>
            {getIconByType(item.type)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="subtitle1"
                fontWeight={!item.read ? "bold" : "normal"}
              >
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(item.dateCreated)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.message}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    ));
  };

  const emptyMessage = (
    <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
      <NotificationsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
      <Typography variant="h6" color="text.secondary">
        No notifications yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        We'll notify you when there's something new
      </Typography>
    </Paper>
  );

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonTitle>News Alerts</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ maxWidth: 900, mx: "auto", p: isMobile ? 1 : 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <NotificationsIcon color="primary" sx={{ fontSize: 32, mr: 2 }} />
            <Typography variant="h5" component="h1">
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Badge badgeContent={unreadCount} color="error" sx={{ ml: 2 }} />
            )}
          </Box>

          <Paper elevation={2} sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="All messages" />
            </Tabs>
          </Paper>

          {status.loading ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" mt={2}>
                Loading notifications...
              </Typography>
            </Box>
          ) : (
            <Box>
              {activeTab === 0 ? (
                notifications.length > 0 ? (
                  <Box sx={{ maxHeight: 500, overflowY: "auto", pr: 1 }}>
                    {renderMessages(notifications)}
                  </Box>
                ) : (
                  emptyMessage
                )
              ) : null}
            </Box>
          )}

          <Dialog
            open={openModal}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {selectedMessage && getIconByType(selectedMessage.type)}
                <Typography variant="h6">{selectedMessage?.title}</Typography>
              </Box>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="caption" color="text.secondary">
                {selectedMessage && formatDate(selectedMessage.dateCreated)}
              </Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                {selectedMessage?.message}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}></DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default NewsAlert;
